import { useSetupNeed } from "@/hooks/use-setup-need";
import {
  SkyButton,
  SkyCard,
  SkyDropdown,
  SkyLink,
  SkyMessageBar,
  SkyRadioGroup,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useId, useMemo, type ReactElement, type ReactNode, type Ref } from "react";
import { Link } from "wouter";
import { usePermissions } from "@/hooks/use-permissions";
import type { ConnectorSummary } from "@shared/types/connectors";
import { DATAFLOW_NAME_MAX, type DataflowSummary } from "@shared/types/dataflows";
import { NOTHING_TO_RUN_ISSUE } from "@shared/types/schedules";
import {
  ConnectionStatusBadge,
  connectionStatusOf,
  connectionStatusPresentation,
} from "@/components/connections/ConnectionStatus";
import { DismissIcon } from "@/lib/icons";
import { ROUTES, absolutePath } from "@/lib/routes";
import { SCHEDULE_RUN_RULE } from "@/lib/schedules/runRule";
import {
  FULL_ONLY_IMPORT_NOTE,
  readsWholeDays,
  ROW_INCREMENTAL_HELP_AFTER_IMPORT,
  ROW_INCREMENTAL_HELP_WHOLE_DAYS,
  WHOLE_DAYS_SENTENCE,
  SOME_TABLES_NOT_IMPORTED,
  firstImportNote,
  hasImported,
} from "@/lib/runs/refreshHint";
import { incrementalCapable, type ScheduleDraft } from "./scheduleForm";

/**
 * Round 4, item 7 (owner: "refresh can be at connections within schedule and not common per
 * schedule"), and round 6, S2 (owner, 2026-10-01: "Connection's setting ... What is that?"): each
 * connection whose CONNECTOR can import incrementally offers "Incremental" or "Full" on its row
 * (`draft.fullRefreshDataflowIds` holds the Full ones). The trigger decides the mode now, not the
 * connection: a connector that cannot import incrementally shows the same row read-only with Full
 * selected, because it imports every row each time whatever the schedule says (round 10, C5).
 * "Set all: Incremental | Full" on the Connections header is the shortcut the old schedule-wide
 * mode was, shown only with three or more connections that can choose.
 *
 * The choice is always offered, never disabled until a first full import (round 6, S3, owner
 * push-back recorded): the runner reads a table whole by itself while nothing has landed
 * (`fellBackToFull`, `runner/config/bundle.ts`), per table and per scope, so a gate would protect
 * nothing. A note under the row says so instead. Only the due scan reads the set; Run now asks on
 * its own.
 */
/** The sentence under a row's chosen answer. #1238: a whole-day connector says what it reads. */
function rowHelp(input: { isFull: boolean; wholeDays: boolean; imported: boolean }): string {
  if (input.isFull) return ROW_REFRESH_HELP.full;
  if (input.wholeDays) {
    return input.imported ? WHOLE_DAYS_SENTENCE : ROW_INCREMENTAL_HELP_WHOLE_DAYS;
  }
  return input.imported ? ROW_INCREMENTAL_HELP_AFTER_IMPORT : ROW_REFRESH_HELP.incremental;
}

/** The two answers on a row, and on the header's "Set all". */
const ROW_REFRESH_OPTIONS = [
  { value: "incremental", label: "Incremental" },
  { value: "full", label: "Full" },
];

/**
 * What the chosen answer means, said under the row. Not the options' own `description`: the
 * library folds that into the radio's accessible name ("FullEvery row again..."), so a screen
 * reader would hear a run-on label and the radios could no longer be found by their word.
 */
export const ROW_REFRESH_HELP: Readonly<Record<"incremental" | "full", string>> = {
  // Owner, 2026-10-01 23:40 (binding): the two modes spelled out on screen.
  incremental:
    "Reads only what changed since the last import. The first import reads everything, because there is nothing to compare with yet.",
  full: "Reads every row on every scheduled import.",
};

/**
 * Under a read-only row: its connector has no incremental mode, so Full is the only answer. #1077:
 * the one sentence Run now and the connection editor say too.
 */
const FULL_ONLY_NOTE = FULL_ONLY_IMPORT_NOTE;

/** Fewest incremental-capable chosen connections for which "Set all" is worth showing. */
const SET_ALL_MIN = 3;

/**
 * What runs: the connections a schedule imports, then (as `children`) the transformations it runs
 * (ported from Studio's `ConfigureScheduleV2`). The second of the editor's three cards since round
 * 6, S4: Schedule name, What runs, When. At least one connection or transformation is required.
 *
 * **The list has no order.** Studio's list was ordered and draggable because
 * its DAG ran connections in sequence. This runner does not: every connection
 * on a schedule gets its extract queued at once, and the capacity gate admits
 * them in parallel up to the instance and per-connector limits
 * (`runner/scheduler/capacity.ts`). An earlier port kept numbered rows and
 * "Move earlier" buttons, which promised a run order nothing keeps (owner
 * feedback, fb-schedules.md §2), so the rows are unnumbered and carry only
 * Remove. `dataflowIds` still stores the order chosen; nothing reads it as a
 * run order.
 *
 * **A not-ready connection is offered and disabled, not hidden.** FR-50 allows
 * only a `ready` connection on a schedule. A picker that simply omitted a draft
 * connection would send the user back to the Connections screen to find out
 * why something they can see there is missing here; naming the state in the
 * option answers it in place.
 *
 * **A disabled connection is the exception: it is never offered** (owner, 2026-09-28:
 * "New Schedule shouldn't show the disabled connection"), and it is left out of the count's
 * total too. A schedule that already names one keeps it, tagged Disabled with "Skipped while
 * disabled": the due scan leaves it out of every firing until it is enabled. Removing it stays
 * the user's choice.
 */

/**
 * A connection's name as the "Add a connection" list shows it: whole up to the connection name
 * limit the connection editor enforces (`DATAFLOW_NAME_MAX`), with an ellipsis only beyond it (a
 * display name can be longer). The option carries the full name as its title (2026-09-28, owner:
 * the list was cut to "Demo SQL ...").
 */
function shownConnectionName(name: string): string {
  return name.length > DATAFLOW_NAME_MAX ? `${name.slice(0, DATAFLOW_NAME_MAX)}…` : name;
}

function fullName(connection: DataflowSummary): string {
  return connection.displayName;
}

export interface ScheduleConnectionsSectionProps {
  draft: ScheduleDraft;
  onChange: (draft: ScheduleDraft) => void;
  connections: DataflowSummary[];
  /**
   * The connector catalog (`GET /connectors`): which connectors can import incrementally (S2)
   * and their display names (W26: "SQL Server", never the key `sqlserver`). Undefined while it
   * loads; a connection whose connector it does not list falls back to its own refresh type.
   */
  connectors?: readonly ConnectorSummary[];
  isLoading?: boolean;
  fieldErrors?: Record<string, string>;
  disabled?: boolean;
  /**
   * Save was pressed while the schedule runs nothing (S4): the reason, shown at the top of this
   * card, which also takes focus. Undefined otherwise.
   */
  nothingToRun?: string;
  /** The Transformations part of What runs, below the connections. */
  children?: ReactNode;
  /** The card, so a failed save can scroll to it and focus it. */
  ref?: Ref<HTMLElement>;
}

/** No connection to list: none in the instance, none Ready to choose, or none chosen yet. */
function NoChosenConnections({
  noneInInstance,
  noneReady,
}: {
  noneInInstance: boolean;
  noneReady: boolean;
}) {
  const { canAny } = usePermissions();
  if (noneInInstance) {
    return (
      <SkyMessageBar
        intent="info"
        layout="multiline"
        title="No connection yet"
        message="This instance has no connection to import from. Create one, or choose transformations only below."
        testId="schedule-no-connections"
        actions={
          canAny("dataflows.write") ? (
            <SkyLink
              as={Link}
              href={absolutePath(`${ROUTES.connections}/new`)}
              testId="schedule-new-connection"
            >
              New connection
            </SkyLink>
          ) : undefined
        }
      />
    );
  }
  return (
    <SkyMessageBar
      intent={noneReady ? "warning" : "info"}
      layout="multiline"
      title={noneReady ? "No connection can be scheduled yet" : "No connection chosen yet"}
      message={
        noneReady
          ? "Validate a connection and select at least one table or add a report, then come back and schedule it. Or choose dbt jobs only below."
          : "No connection. Add one to import data, or choose transformations only below."
      }
      testId="schedule-no-connections"
    />
  );
}

/**
 * S2: how the row imports. The same Incremental/Full row for every connection; one whose connector
 * cannot import incrementally is read-only with Full selected.
 */
function rowRefreshChoice({
  id,
  name,
  capable,
  isFull,
  disabled,
  onRefreshChange,
}: {
  id: string;
  name: string;
  capable: boolean;
  isFull: boolean;
  disabled: boolean;
  onRefreshChange: (full: boolean) => void;
}): ReactElement {
  return (
    <SkyRadioGroup
      ariaLabel={`How ${name} imports on a scheduled run`}
      layout="horizontal"
      size="small"
      options={ROW_REFRESH_OPTIONS}
      value={capable && !isFull ? "incremental" : "full"}
      onChange={(value) => onRefreshChange(value === "full")}
      disabled={disabled || !capable}
      className="mt-1"
      testId={`schedule-connection-refresh-${id}`}
    />
  );
}

/** One chosen connection: its name and connector, how it imports, its notes, its badge, Remove. */
function ChosenConnectionRow({
  id,
  connection,
  capable,
  wholeDays,
  isFull,
  connectorLabel,
  disabled,
  onRefreshChange,
  onRemove,
}: {
  /** #1238: its connector re-reads whole days. */
  wholeDays: boolean;
  id: string;
  connection: DataflowSummary | undefined;
  /** S2: its connector can import incrementally, so the row offers the choice. */
  capable: boolean;
  isFull: boolean;
  connectorLabel: (connection: DataflowSummary) => string;
  disabled: boolean;
  onRefreshChange: (full: boolean) => void;
  onRemove: () => void;
}) {
  const name = connection?.displayName ?? id;
  const need = useSetupNeed(connection?.connectorName, connection?.selectedEntityCount);
  const state = connection?.state ?? "draft";
  const disabledHere = state === "disabled";
  const note = connection && capable && !isFull ? firstImportNote(connection, wholeDays) : null;
  return (
    <li
      className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
      data-testid={`schedule-connection-${id}`}
    >
      <div className="min-w-0 flex-1">
        <SkyText size={300} weight="semibold" block truncate>
          <span title={name}>{name}</span>
        </SkyText>
        <SkyText size={200} color="muted" block testId={`schedule-connection-connector-${id}`}>
          {connection
            ? connectorLabel(connection)
            : "This connection is no longer in this instance"}
        </SkyText>
        {connection
          ? rowRefreshChoice({ id, name, capable, isFull, disabled, onRefreshChange })
          : null}
        {connection && capable ? (
          <SkyText size={200} color="muted" block testId={`schedule-connection-refresh-help-${id}`}>
            {rowHelp({
              isFull,
              wholeDays,
              imported: hasImported(connection) || note === SOME_TABLES_NOT_IMPORTED,
            })}
          </SkyText>
        ) : null}
        {connection && !capable ? (
          <SkyText size={200} color="muted" block testId={`schedule-connection-full-only-${id}`}>
            {FULL_ONLY_NOTE}
          </SkyText>
        ) : null}
        {note ? (
          <SkyText
            size={200}
            color="muted"
            block
            className="mt-1"
            testId={`schedule-connection-first-import-${id}`}
          >
            {note}
          </SkyText>
        ) : null}
        {disabledHere ? (
          <SkyText size={200} color="muted" block testId={`schedule-connection-skipped-${id}`}>
            Skipped while disabled
          </SkyText>
        ) : null}
      </div>
      {/* #1007: the Connections list's own word and colour (`connectionStatusOf`), never the raw
          state. Whether it can be scheduled is a separate gate (FR-50). */}
      <ConnectionStatusBadge
        status={connection ? connectionStatusOf(connection) : "setup_incomplete"}
        need={need}
        testId={`schedule-connection-status-${id}`}
      />
      <SkyButton
        appearance="subtle"
        size="small"
        icon={DismissIcon}
        disabled={disabled}
        aria-label={`Remove ${name} from this schedule`}
        onClick={onRemove}
      />
    </li>
  );
}

function addPlaceholder(noneInInstance: boolean, availableCount: number): string {
  if (noneInInstance) return "No connection in this instance yet";
  return availableCount === 0 ? "Every connection is already added" : "Choose one";
}

/** S1: the one refusal on this field that is not about a named connection. */
function dataflowErrorTitle(issue: string): string {
  return issue === NOTHING_TO_RUN_ISSUE
    ? "Nothing to run"
    : "This schedule names a connection that cannot run";
}

/** "Set all: Incremental | Full" on the Connections header; neither is selected while mixed. */
function SetAllControl({
  disabled,
  allFull,
  noneFull,
  onSetAll,
}: {
  disabled: boolean;
  allFull: boolean;
  noneFull: boolean;
  onSetAll: (full: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-2" data-testid="schedule-refresh-set-all">
      <SkyText size={200} color="muted">
        Set all:
      </SkyText>
      <SkyRadioGroup
        ariaLabel="Set all connections that support it to"
        layout="horizontal"
        size="small"
        options={ROW_REFRESH_OPTIONS}
        value={allFull ? "full" : noneFull ? "incremental" : ""}
        onChange={(value) => onSetAll(value === "full")}
        disabled={disabled}
        testId="schedule-refresh-set-all-choice"
      />
    </div>
  );
}

export function ScheduleConnectionsSection({
  draft,
  onChange,
  connections,
  connectors,
  isLoading = false,
  fieldErrors = {},
  disabled = false,
  nothingToRun,
  children,
  ref,
}: ScheduleConnectionsSectionProps) {
  const headingId = useId();
  const byId = useMemo(
    () => new Map(connections.map((connection) => [connection.id, connection])),
    [connections],
  );
  const connectorByName = useMemo(
    () => new Map((connectors ?? []).map((connector) => [connector.name, connector])),
    [connectors],
  );

  const chosen = draft.dataflowIds;
  // A disabled connection is never offered and never counted (2026-09-28).
  const schedulable = connections.filter((connection) => connection.state !== "disabled");
  const available = schedulable.filter((connection) => !chosen.includes(connection.id));
  const readyCount = schedulable.filter((c) => c.state === "ready").length;
  // W11: an instance with no connection at all says so and links to New connection.
  const noneInInstance = !isLoading && connections.length === 0;
  // S2: the rows that offer a choice are the ones whose connector can import incrementally.
  const capable = (connection: DataflowSummary | undefined): boolean =>
    connection ? incrementalCapable(connection, connectorByName) : false;
  // #1238: its connector re-reads whole days, so its help names the days it reads.
  const wholeDaysOf = (connection: DataflowSummary | undefined): boolean =>
    connection ? readsWholeDays(connection, connectorByName) : false;
  const incrementalIds = chosen.filter((id) => capable(byId.get(id)));
  const fullSet = new Set(draft.fullRefreshDataflowIds);
  const allFull = incrementalIds.length > 0 && incrementalIds.every((id) => fullSet.has(id));
  const noneFull = incrementalIds.every((id) => !fullSet.has(id));
  const showSetAll = incrementalIds.length >= SET_ALL_MIN;
  const setAllError = fieldErrors.fullRefreshDataflowIds ?? fieldErrors.refreshMode;

  const setFull = (ids: readonly string[], full: boolean): void => {
    const next = new Set(draft.fullRefreshDataflowIds);
    for (const id of ids) {
      if (full) next.add(id);
      else next.delete(id);
    }
    onChange({ ...draft, fullRefreshDataflowIds: chosen.filter((id) => next.has(id)) });
  };

  const remove = (id: string): void => {
    onChange({
      ...draft,
      dataflowIds: chosen.filter((existing) => existing !== id),
      fullRefreshDataflowIds: draft.fullRefreshDataflowIds.filter((existing) => existing !== id),
    });
  };

  const add = (values: string[]): void => {
    const id = values[0];
    if (!id || chosen.includes(id)) return;
    onChange({ ...draft, dataflowIds: [...chosen, id] });
  };

  /** W26: the connector's display name, never its key, unless the catalog does not list it. */
  const connectorLabel = (connection: DataflowSummary): string =>
    connectorByName.get(connection.connectorName)?.displayName ?? connection.connectorName;

  return (
    <section
      ref={ref}
      tabIndex={-1}
      aria-labelledby={headingId}
      data-testid="schedule-configure-section"
      className="rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <SkyCard
        appearance="outline"
        header={{
          title: (
            <SkyText as="h2" id={headingId} size={400} weight="semibold">
              What runs
            </SkyText>
          ),
          description: "Choose connections, dbt jobs, or both. At least one is required.",
        }}
      >
        {nothingToRun ? (
          <SkyText
            size={300}
            color="danger"
            block
            role="alert"
            testId="schedule-what-runs-required"
          >
            {nothingToRun}
          </SkyText>
        ) : null}

        <div className="flex flex-col gap-3" data-testid="schedule-connections-part">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <SkyText as="h3" size={300} weight="semibold">
              Connections
            </SkyText>
            {showSetAll ? (
              <SetAllControl
                disabled={disabled}
                allFull={allFull}
                noneFull={noneFull}
                onSetAll={(full) => setFull(incrementalIds, full)}
              />
            ) : null}
          </div>
          {setAllError ? (
            <SkyText size={200} color="danger" block role="alert" testId="schedule-refresh-error">
              {setAllError}
            </SkyText>
          ) : null}
          {/* One rule with the Run now dialog (#1203): the runner queues every connection's
              extract at once, admits them up to the limits, then runs the dbt jobs. */}
          <SkyText size={200} color="muted" block testId="schedule-connections-parallel">
            {SCHEDULE_RUN_RULE}
          </SkyText>

          {noneInInstance || chosen.length === 0 ? (
            <NoChosenConnections
              noneInInstance={noneInInstance}
              noneReady={readyCount === 0 && !isLoading}
            />
          ) : (
            <ul className="flex w-full flex-col gap-2" data-testid="schedule-connection-list">
              {chosen.map((id) => (
                <ChosenConnectionRow
                  key={id}
                  id={id}
                  connection={byId.get(id)}
                  capable={capable(byId.get(id))}
                  wholeDays={wholeDaysOf(byId.get(id))}
                  isFull={fullSet.has(id)}
                  connectorLabel={connectorLabel}
                  disabled={disabled}
                  onRefreshChange={(full) => setFull([id], full)}
                  onRemove={() => remove(id)}
                />
              ))}
            </ul>
          )}

          {fieldErrors.dataflowIds ? (
            <div>
              <SkyMessageBar
                intent="error"
                layout="multiline"
                title={dataflowErrorTitle(fieldErrors.dataflowIds)}
                message={fieldErrors.dataflowIds}
                testId="schedule-dataflow-error"
              />
            </div>
          ) : null}

          {/* The full width of the card, as the list of chosen connections: the options are as
              wide as the trigger, so a trigger sized to its placeholder cut every name short
              (2026-09-28). */}
          <SkyDropdown
            label="Add a connection"
            placeholder={addPlaceholder(noneInInstance, available.length)}
            options={available.map((connection) => {
              const shown = shownConnectionName(fullName(connection));
              return {
                value: connection.id,
                // The gate is the setup state, so the reason is too: never the run-merged status
                // (a draft with an import in flight would read "Running").
                text:
                  connection.state === "ready"
                    ? shown
                    : `${shown} (${connectionStatusPresentation("setup_incomplete").label}, cannot be scheduled)`,
                disabled: connection.state !== "ready",
              };
            })}
            // The title is the full name: for a name the ellipsis shortened, and for a card
            // narrower than the name (a phone), where the option truncates too.
            optionTemplate={(option) => {
              const connection = byId.get(option.value);
              return (
                <span
                  className="block truncate"
                  title={connection ? fullName(connection) : option.text}
                >
                  {option.text}
                </span>
              );
            }}
            selectedOptions={[]}
            onOptionSelect={add}
            loading={isLoading}
            disabled={disabled || available.length === 0}
            listboxMaxHeight="16rem"
            className="w-full"
            testId="schedule-add-connection"
          />
          {noneInInstance ? null : (
            <SkyText size={200} color="muted" block testId="schedule-connections-count">
              {readyCount} of {schedulable.length} connections can be scheduled (setup complete).
            </SkyText>
          )}
        </div>

        {children ? <div className="mt-2 border-t border-border pt-4">{children}</div> : null}
      </SkyCard>
    </section>
  );
}
